import { Injectable, inject, signal, computed } from "@angular/core";
import { Observable, catchError, map, take, of } from "rxjs";
import { MessageService } from "primeng/api";
import { HttpProductsService } from "../../shared/services/http-products.service";
import {
  ScannedProductsService,
  ScannedProduct,
} from "./scanned-products.service";
import { ScanIframeService } from "./scan-iframe.service";
import {
  ProductAddedEvent,
  ProductRemovedEvent,
  ProductQuantityChangedEvent,
} from "../models/scan-iframe.contract";
import { normalizeBarcode, barcodesMatch } from "../utils/barcode.utils";
import { ScanSessionTimeoutService } from "./scan-session-timeout.service";

export interface BarcodeScanResult {
  success: boolean;
  product?: ScannedProduct;
  barcode: string;
  error?: {
    code: string;
    message: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class BarcodeScanningService {
  private httpProductsService = inject(HttpProductsService);
  private scannedProductsService = inject(ScannedProductsService);
  private scanIframeService = inject(ScanIframeService);
  private messageService = inject(MessageService);
  private sessionTimeoutService = inject(ScanSessionTimeoutService);

  // Scanning state
  private isScanning = signal<boolean>(false);
  private lastScanResult = signal<BarcodeScanResult | null>(null);

  // Track barcodes currently being processed to prevent duplicate processing
  private processingBarcodes = new Set<string>();

  // Computed properties
  readonly scanning = computed(() => this.isScanning());
  readonly lastResult = computed(() => this.lastScanResult());
  readonly scannedProducts = this.scannedProductsService.products;
  readonly hasProducts = this.scannedProductsService.hasProducts;
  readonly productCount = this.scannedProductsService.productCount;
  readonly lastScannedProduct = this.scannedProductsService.lastScannedProduct;

  // Context from parent iframe (manufacturer_id, vendor_id, store_id)
  readonly context = this.scanIframeService.context;
  readonly manufacturerId = computed(() => {
    const context = this.scanIframeService.context();
    return context?.manufacturer_id?.toString() || "";
  });
  readonly vendorId = computed(() => {
    const context = this.scanIframeService.context();
    return context?.vendor_id?.toString() || "";
  });
  readonly storeId = computed(() => {
    const context = this.scanIframeService.context();
    return context?.store_id?.toString() || "";
  });

  /**
   * Process a scanned barcode - this is the main entry point for any consumer component
   * @param barcode - The scanned barcode data
   * @param scanMode - The scanning mode ('native' or 'camera')
   * @returns Observable with the scan result
   */
  processBarcode(
    barcode: string,
    scanMode: "native" | "camera" = "camera",
  ): Observable<BarcodeScanResult> {
    // Reset idle timer - user is actively scanning
    this.sessionTimeoutService.resetTimers();

    // Check if this barcode is already being processed (API call in flight)
    if (this.processingBarcodes.has(barcode)) {
      console.log(
        `Barcode ${barcode} is already being processed, skipping duplicate.`,
      );
      this.messageService.add({
        severity: "warn",
        summary: "Product already scanned",
        detail: `UPC: ${barcode} is already in the list.`,
        life: 5000,
      });
      return of({
        success: false,
        barcode,
        error: {
          code: "ALREADY_PROCESSING",
          message: `Product already scanned: ${barcode}`,
        },
      });
    }

    // Check if product with this barcode is already in scanned products list
    // This prevents unnecessary API calls for already-scanned products
    const existingProduct = this.findExistingProductByBarcode(barcode);
    if (existingProduct) {
      const enableQuantities =
        this.scanIframeService.getCurrentConfig()?.features?.enableQuantities ??
        false;

      if (enableQuantities) {
        // Quantities enabled: increment quantity for the existing product
        this.scannedProductsService.incrementQuantity(
          existingProduct.product.upc.toString(),
        );
        const updatedProduct = this.scannedProductsService.getProductBySku(
          existingProduct.product.upc.toString(),
        );

        this.messageService.add({
          severity: "info",
          summary: "Product already scanned",
          detail: `UPC: ${barcode} is already in the list.`,
          life: 5000,
        });

        return of({
          success: true,
          barcode,
          product: updatedProduct,
        });
      } else {
        this.messageService.add({
          severity: "warn",
          summary: "Product already scanned",
          detail: `UPC: ${barcode} is already in the list.`,
          life: 5000,
        });

        return of({
          success: false,
          barcode,
          error: {
            code: "ALREADY_SCANNED",
            message: `Product already scanned: ${existingProduct.product.description || barcode}`,
          },
        });
      }
    }

    // Mark barcode as being processed
    this.processingBarcodes.add(barcode);
    this.isScanning.set(true);

    console.log(`Processing barcode: ${barcode} in ${scanMode} mode`);

    // Look up product by barcode/SKU
    return this.httpProductsService
      .getProductByUpc(
        barcode,
        this.manufacturerId(),
        this.vendorId(),
        this.storeId(),
      )
      .pipe(
        take(1),
        map((product) => {
          // Remove from processing set
          this.processingBarcodes.delete(barcode);

          // Check if product already exists before adding
          const productExists = this.scannedProductsService.hasProduct(
            product.upc?.toString(),
          );
          const scannedProduct = this.scannedProductsService.addProduct(
            product,
            barcode,
            scanMode,
          );

          const result: BarcodeScanResult = {
            success: true,
            product: scannedProduct,
            barcode,
          };

          this.lastScanResult.set(result);
          this.isScanning.set(false);

          // Only notify PRODUCT_ADDED for new products
          // If product existed, incrementQuantity() already sent PRODUCT_QUANTITY_CHANGED
          if (!productExists) {
            this.notifyProductAdded(scannedProduct, barcode, scanMode);
          }

          console.log("Product found and added:", product);
          return result;
        }),
        catchError((error) => {
          // Remove from processing set on error
          this.processingBarcodes.delete(barcode);

          // Error - product not found or API error
          const backendMessage =
            typeof error?.error?.error === "string" ? error.error.error : null;
          const fallbackMessage = `No product found for barcode: ${barcode}`;
          const errorMessage = backendMessage ?? fallbackMessage;

          const result: BarcodeScanResult = {
            success: false,
            barcode,
            error: {
              code: "PRODUCT_NOT_FOUND",
              message: errorMessage,
            },
          };

          this.lastScanResult.set(result);
          this.isScanning.set(false);

          // Notify parent iframe about failed scan
          this.notifyScanError(barcode, error);

          this.messageService.add({
            severity: "error",
            summary: "Product not found",
            detail: errorMessage,
          });

          // Emit a failure result instead of throwing so consumers can decide how to present it
          // (prevents duplicate toasts in native scanning).
          return of(result);
        }),
      );
  }

  /**
   * Increment quantity for a product
   * @param sku - The SKU of the product to increment
   */
  incrementQuantity(sku: string): void {
    this.scannedProductsService.incrementQuantity(sku);
  }

  /**
   * Decrement quantity for a product
   * @param sku - The SKU of the product to decrement
   */
  decrementQuantity(sku: string): void {
    const productToRemove = this.scannedProductsService.getProductBySku(sku);
    if (!productToRemove) return;

    const previousQuantity = productToRemove.quantity || 1;
    const updatedProduct = this.scannedProductsService.decrementQuantity(sku);

    if (updatedProduct) {
      // Quantity decremented successfully
      this.notifyQuantityChanged(
        updatedProduct,
        previousQuantity,
        updatedProduct.quantity || 1,
        "decrement",
      );
    } else {
      // Product was removed because quantity reached 0
      this.notifyProductRemoved(productToRemove);
      console.log("Product removed:", productToRemove);
    }
  }

  /**
   * Update quantity for a product
   * @param sku - The SKU of the product to update
   * @param quantity - The new quantity
   */
  updateQuantity(sku: string, quantity: number): void {
    const product = this.scannedProductsService.getProductBySku(sku);
    if (!product) return;

    const previousQuantity = product.quantity || 1;
    const updatedProduct = this.scannedProductsService.updateQuantity(
      sku,
      quantity,
    );

    if (updatedProduct) {
      // Quantity updated successfully
      this.notifyQuantityChanged(
        updatedProduct,
        previousQuantity,
        updatedProduct.quantity || 1,
        "update",
      );
    } else {
      // Product was removed because quantity was 0 or less
      this.notifyProductRemoved(product);
      console.log("Product removed due to quantity update to 0:", product);
    }
  }

  /**
   * Remove a product from scanned products
   * @param sku - The SKU of the product to remove
   */
  removeProduct(sku: string): void {
    const product = this.scannedProductsService.getProductBySku(sku);
    if (product) {
      this.scannedProductsService.removeProduct(sku);
      this.notifyProductRemoved(product);
      console.log("Product removed:", product);
    }
  }

  /**
   * Remove the last scanned product
   */
  removeLastProduct(): void {
    const lastProduct = this.scannedProductsService.lastScannedProduct();
    if (lastProduct) {
      this.scannedProductsService.removeLastProduct();
      this.notifyProductRemoved(lastProduct);
    }
  }

  /**
   * Clear all scanned products
   */
  clearAllProducts(): void {
    this.scannedProductsService.clearAll();
  }

  /**
   * Get products scanned in a specific mode
   * @param scanMode - The scan mode to filter by
   */
  getProductsByMode(scanMode: "native" | "camera"): ScannedProduct[] {
    return this.scannedProductsService.getProductsByMode(scanMode);
  }

  /**
   * Check if a product exists by SKU
   * @param sku - The SKU to check
   */
  hasProduct(sku: string): boolean {
    return this.scannedProductsService.hasProduct(sku);
  }

  /**
   * Get a product by SKU
   * @param sku - The SKU to search for
   */
  getProductBySku(sku: string): ScannedProduct | undefined {
    return this.scannedProductsService.getProductBySku(sku);
  }

  /**
   * Find an existing product by barcode with flexible matching
   * Handles EAN-13 vs UPC-A format differences (leading zeros)
   * @param barcode - The barcode to search for
   * @returns The found product or undefined
   */
  private findExistingProductByBarcode(
    barcode: string,
  ): ScannedProduct | undefined {
    const scannedProducts = this.scannedProductsService.products();

    // Direct key lookup
    if (scannedProducts[barcode]) {
      return scannedProducts[barcode];
    }

    // Normalized key lookup (remove leading zeros)
    const normalized = normalizeBarcode(barcode);
    if (scannedProducts[normalized]) {
      return scannedProducts[normalized];
    }

    // Search through products using flexible matching
    return Object.values(scannedProducts).find(
      (p) =>
        barcodesMatch(barcode, p.barcode) ||
        barcodesMatch(barcode, p.product.upc?.toString()),
    );
  }

  // Private methods for iframe notifications

  private notifyProductAdded(
    scannedProduct: ScannedProduct,
    barcode: string,
    scanMode: "native" | "camera",
  ): void {
    // Send product added event with nested Product data
    const productAddedEvent: ProductAddedEvent = {
      product: scannedProduct.product,
      barcode: barcode,
      timestamp: scannedProduct.timestamp.toISOString(),
      scanMode: scanMode,
      quantity: scannedProduct.quantity,
    };
    this.scanIframeService.sendProductAdded(productAddedEvent);
  }

  private notifyProductRemoved(scannedProduct: ScannedProduct): void {
    const productRemovedEvent: ProductRemovedEvent = {
      product: scannedProduct.product,
      barcode: scannedProduct.barcode,
      timestamp: scannedProduct.timestamp.toISOString(),
      scanMode: scannedProduct.scanMode,
      quantity: scannedProduct.quantity,
    };

    this.scanIframeService.sendProductRemoved(productRemovedEvent);
  }

  private notifyQuantityChanged(
    scannedProduct: ScannedProduct,
    previousQuantity: number,
    newQuantity: number,
    changeType: "increment" | "decrement" | "update",
  ): void {
    const quantityChangedEvent: ProductQuantityChangedEvent = {
      product: scannedProduct.product,
      barcode: scannedProduct.barcode,
      timestamp: scannedProduct.timestamp.toISOString(),
      scanMode: scannedProduct.scanMode,
      quantity: scannedProduct.quantity,
      quantityChange: {
        previousQuantity,
        newQuantity,
        changeType,
      },
    };

    this.scanIframeService.sendProductQuantityChanged(quantityChangedEvent);
  }

  private notifyScanError(barcode: string, error: unknown): void {
    this.scanIframeService.sendError(
      this.scanIframeService.ScanErrorCodes.PRODUCT_NOT_FOUND,
      `No product found for barcode: ${barcode}`,
      error,
    );
  }
}
